import { useNavigate, usePath } from '../session';
import { palette } from '../theme';
import { AssetViewer } from './assets/AssetViewer';
import { SquirrelDesigner } from './squirrel/SquirrelDesigner';
import { TreesViewer } from './trees/TreesViewer';

// ---------------------------------------------------------------------------
// Tools host shell. Lives inside SessionGuard, so by the time we render
// here the user is connected and has a User row. Per the spec
// (DOCS/SPECS/in_game_tooling.md), there is no additional admin gate in v1.
//
// Adding a new tool: create client/src/tools/<name>/, add a route case
// below, add a TopBar entry. No central registry.
// ---------------------------------------------------------------------------

type ActiveTool = 'assets' | 'squirrel' | 'trees' | 'unknown';

function classifyPath(path: string): ActiveTool {
  if (path === '/tools' || path === '/tools/' || path.startsWith('/tools/assets')) {
    return 'assets';
  }
  if (path.startsWith('/tools/squirrel')) return 'squirrel';
  if (path.startsWith('/tools/trees')) return 'trees';
  return 'unknown';
}

export function ToolsRoute() {
  const path = usePath();
  const active = classifyPath(path);

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        background: palette.barkBrown,
        color: palette.warmBeige,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <TopBar active={active} />
      <main style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {active === 'assets' && <AssetViewer />}
        {active === 'squirrel' && <SquirrelDesigner />}
        {active === 'trees' && <TreesViewer />}
        {active === 'unknown' && <NotFound path={path} />}
      </main>
    </div>
  );
}

function TopBar({ active }: { active: ActiveTool }) {
  const nav = useNavigate();
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '8px 16px',
        background: 'rgba(0, 0, 0, 0.35)',
        borderBottom: `1px solid ${palette.mossTeal}`,
        fontSize: 13,
      }}
    >
      <strong style={{ letterSpacing: 1 }}>Heartwood · Tools</strong>
      <nav style={{ display: 'flex', gap: 6 }}>
        <ToolLink label="Asset viewer" href="/tools/assets" active={active === 'assets'} />
        <ToolLink
          label="Squirrel designer"
          href="/tools/squirrel"
          active={active === 'squirrel'}
        />
        <ToolLink label="Tree editor" href="/tools/trees" active={active === 'trees'} />
      </nav>
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={() => nav('/play')}
        style={linkButton}
      >
        ← Back to canopy
      </button>
      <button
        type="button"
        onClick={() => nav('/')}
        style={linkButton}
      >
        Landing
      </button>
    </header>
  );
}

function ToolLink({
  label,
  href,
  active,
  disabled,
  tooltip,
}: {
  label: string;
  href?: string;
  active?: boolean;
  disabled?: boolean;
  tooltip?: string;
}) {
  const nav = useNavigate();
  const base: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: 4,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontFamily: 'system-ui, sans-serif',
  };
  if (disabled) {
    return (
      <span
        title={tooltip}
        style={{
          ...base,
          opacity: 0.4,
          background: 'transparent',
          color: palette.warmBeige,
        }}
      >
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => href && nav(href)}
      style={{
        ...base,
        background: active ? palette.acornOrange : 'rgba(255,255,255,0.05)',
        color: active ? palette.barkBrown : palette.warmBeige,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

const linkButton: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${palette.mossTeal}`,
  color: palette.warmBeige,
  padding: '4px 10px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'system-ui, sans-serif',
};

function NotFound({ path }: { path: string }) {
  return (
    <div style={messageBox}>
      <h2 style={{ margin: 0 }}>Unknown tool</h2>
      <p style={{ margin: '8px 0 0', opacity: 0.75 }}>
        No tool registered at <code>{path}</code>.
      </p>
    </div>
  );
}

const messageBox: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 32,
};
